import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, X } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Mono,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from "@lagoon/ui";

import { api, type Me, type ScreeningFinding, type ScreeningRelease } from "../api.js";
import { ScanStateBadge } from "../components/badges.js";
import { ReasonAction } from "../components/reason-action.js";

/**
 * The screening review queue (screening S1, LGN-79).
 *
 * `docs/CONTENT-SCREENING-PLAN.md` §4.5 gives heuristics one outcome and one
 * only: they flag, they never block. That is the right call, because a rule
 * strict enough to refuse a release would refuse legitimate creator sites too.
 * It only works if somebody looks, and this is where they look.
 *
 * Two actions, and the screen makes the difference between them plain because
 * an operator picking the wrong one has either published malware or taken a
 * customer's site down:
 *
 *   Clear  the findings are fine. Nothing about the release changes; the
 *          findings stay exactly as the scan wrote them. It leaves the queue.
 *   Block  the release is refused, for good, and if it is the live one the
 *          site stops answering in the same transaction.
 *
 * There is deliberately no "unblock". `publishDeploy` refuses a blocked release
 * whatever the kill switch says, and offering a button that appeared to undo
 * that would be a way to publish known-bad content by clicking twice. The way
 * forward from a block is a new release.
 */
export function ScreeningScreen({ me }: { me: Me }): React.JSX.Element {
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: ["screening", includeReviewed],
    queryFn: () => api.screeningQueue(includeReviewed),
  });

  const columns = useReleaseColumns({
    canOperate: me.can.operate,
    selected,
    onSelect: setSelected,
  });

  const open = queue.data?.releases.find((release) => release.deployId === selected);

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Safety"
        title="Screening review"
        description="Releases the upload heuristics flagged, and releases somebody has blocked."
      />

      <Alert tone="warning">
        <AlertTitle>Everything here is already serving</AlertTitle>
        <AlertDescription>
          A flagged release <strong>is published and is being served</strong>: the heuristics queue
          a look, they never refuse a deploy, so anything on this screen is live unless the site
          says otherwise.
        </AlertDescription>
      </Alert>

      <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
        <Button
          variant={includeReviewed ? "ghost" : "subtle"}
          size="sm"
          aria-pressed={!includeReviewed}
          onClick={() => {
            setIncludeReviewed(false);
            setSelected(null);
          }}
        >
          Needs review
        </Button>
        <Button
          variant={includeReviewed ? "subtle" : "ghost"}
          size="sm"
          aria-pressed={includeReviewed}
          onClick={() => {
            setIncludeReviewed(true);
            setSelected(null);
          }}
        >
          Including reviewed
        </Button>
      </div>

      {queue.error && (
        <Text size="sm" tone="danger" role="alert">
          {queue.error.message}
        </Text>
      )}

      <DataTable
        columns={columns}
        data={queue.data?.releases ?? []}
        loading={queue.isPending}
        caption="Releases waiting on a screening decision"
        empty={
          <EmptyState
            icon={<ShieldCheck />}
            title="Nothing is waiting"
            description={
              <>
                That is either a quiet week or a screening engine that is switched off: check{" "}
                <Mono>screening.engine</Mono> on the{" "}
                <a href="#/flags" className="lg:text-primary lg:hover:underline">
                  Flags screen
                </a>
                .
              </>
            }
          />
        }
      />

      {open !== undefined && (
        <FindingsPanel
          release={open}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function useReleaseColumns({
  canOperate,
  selected,
  onSelect,
}: {
  canOperate: boolean;
  selected: string | null;
  onSelect: (deployId: string | null) => void;
}): DataTableColumn<ScreeningRelease>[] {
  return useMemo(() => {
    const columns: DataTableColumn<ScreeningRelease>[] = [
      {
        accessorKey: "slug",
        header: "Site",
        cell: (info) => (
          <div className="lg:flex lg:flex-col">
            <a
              href={`#/site/${info.row.original.siteId}`}
              className="lg:whitespace-nowrap lg:font-mono lg:text-primary lg:hover:underline"
            >
              {info.row.original.slug}
            </a>
            <Text size="caption" tone="subtle">
              {info.row.original.siteName}
            </Text>
          </div>
        ),
      },
      {
        accessorKey: "ownerEmail",
        header: "Owner",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {info.row.original.ownerEmail}
          </Mono>
        ),
      },
      {
        accessorKey: "scanState",
        header: "State",
        cell: (info) => <ScanStateBadge state={info.row.original.scanState} />,
      },
      {
        id: "published",
        header: "Live",
        cell: (info) =>
          info.row.original.published ? <Badge tone="success">Serving</Badge> : null,
      },
      {
        id: "rules",
        header: "Rules",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {info.row.original.rules.join(", ")}
          </Mono>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Uploaded",
        sortFn: "datetime",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {new Date(info.row.original.createdAt).toLocaleString()}
          </Mono>
        ),
      },
      {
        id: "findings",
        header: "Findings",
        cell: (info) => {
          const release = info.row.original;
          const isOpen = release.deployId === selected;
          return (
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={isOpen}
              onClick={() => {
                onSelect(isOpen ? null : release.deployId);
              }}
            >
              {isOpen ? "Hide" : `${String(release.findingCount)} finding(s)`}
            </Button>
          );
        },
      },
    ];

    // The actions column is ABSENT for an auditor rather than disabled. A
    // disabled button says "you could do this if you tried harder"; a missing
    // one says the account is not for that, which is the truth.
    if (canOperate) {
      columns.push({
        id: "actions",
        header: "Actions",
        cell: (info) => <ReleaseActions release={info.row.original} />,
      });
    }

    return columns;
  }, [canOperate, selected, onSelect]);
}

function ReleaseActions({ release }: { release: ScreeningRelease }): React.JSX.Element {
  const queryClient = useQueryClient();
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["screening"] });
    await queryClient.invalidateQueries({ queryKey: ["health"] });
  };

  return (
    <div className="lg:flex lg:flex-wrap lg:gap-2">
      {release.scanState !== "blocked" && release.reviewedAt === null && (
        <ReasonAction
          label="Clear"
          compact
          hint="The findings are fine and the release may stand. Nothing about the release changes; this records that a person read them."
          onSubmit={async (reason) => {
            await api.clearScreening(release.deployId, reason);
            await refresh();
          }}
        />
      )}
      {release.scanState !== "blocked" && (
        <ReasonAction
          label="Block"
          danger
          compact
          hint={
            release.published
              ? "Refuses this release for good AND takes the site off the air, in one transaction. There is no way back except a new release."
              : "Refuses this release for good. There is no way back except a new release."
          }
          onSubmit={async (reason) => {
            await api.blockScreening(release.deployId, reason);
            await refresh();
          }}
        />
      )}
      {release.scanState === "blocked" && release.reviewedAt === null && (
        <ReasonAction
          label="Mark reviewed"
          compact
          hint="Blocked already; this only takes it out of the queue."
          onSubmit={async (reason) => {
            // Blocking an already-blocked release is the idempotent way to
            // stamp the review. `clear` refuses a blocked release on purpose,
            // so that an operator cannot walk one back with the softer button.
            await api.blockScreening(release.deployId, reason);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

const SEVERITY_TONE = {
  block: "danger",
  flag: "warning",
  info: "info",
} as const;

/**
 * Every finding on one release, loaded only when somebody asks for it.
 *
 * A panel under the table rather than an extra row inside it. The findings are
 * a different shape from the queue: four columns of their own, and a detail
 * blob that can be long: and folding them into a row made both tables harder
 * to read than either is alone.
 */
function FindingsPanel({
  release,
  onClose,
}: {
  release: ScreeningRelease;
  onClose: () => void;
}): React.JSX.Element {
  const review = useQuery({
    queryKey: ["screening", "review", release.deployId],
    queryFn: () => api.screeningReview(release.deployId),
  });

  return (
    <Card>
      <CardHeader className="lg:flex-row lg:items-start lg:justify-between lg:gap-4">
        <div>
          <CardTitle>
            Findings on <Mono>{release.slug}</Mono>
          </CardTitle>
          <Text size="caption" tone="subtle" className="lg:mt-1">
            Release <Mono>{release.deployId.slice(0, 8)}</Mono>
          </Text>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X />
          Close
        </Button>
      </CardHeader>
      <CardContent>
        {review.isPending && (
          <Text size="sm" tone="muted">
            Loading findings…
          </Text>
        )}
        {review.error && (
          <Text size="sm" tone="danger" role="alert">
            {review.error.message}
          </Text>
        )}
        {review.data && (
          <TableRoot>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Severity</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {review.data.findings.map((finding) => (
                  <FindingRow key={finding.id} finding={finding} />
                ))}
              </TableBody>
            </Table>
          </TableRoot>
        )}
      </CardContent>
    </Card>
  );
}

function FindingRow({ finding }: { finding: ScreeningFinding }): React.JSX.Element {
  return (
    <TableRow>
      <TableCell>
        <Badge tone={SEVERITY_TONE[finding.severity]}>{finding.severity}</Badge>
      </TableCell>
      <TableCell>
        <Mono className="lg:text-caption">{finding.rule}</Mono>
      </TableCell>
      {/*
        Paths and rule ids are rendered as text, and React escapes them, which is
        what makes that safe: an entry name is the uploader's, and the platform
        sanitises it before it is stored as well. Never dangerouslySetInnerHTML
        here, whatever the detail looks like.
      */}
      <TableCell className="lg:text-muted-foreground">{finding.path ?? "(the archive)"}</TableCell>
      <TableCell>
        <Mono className="lg:text-caption lg:text-muted-foreground">
          {JSON.stringify(finding.detail)}
        </Mono>
      </TableCell>
    </TableRow>
  );
}
